import { Box, styled } from '@mui/material';
import { FlexBox } from 'components/Structure';
import Image from 'designSystem/DataDisplay/Image/Image';
import CardContainer from 'designSystem/Layout/CardContainer/CardContainer';
import Icon from 'designSystem/Primitives/Icon/Icon';
import ThemeTypography from 'designSystem/Primitives/Typography/ThemeTypography';
import React, { FC } from 'react';
import { Booleanish, booleanish } from 'types/booleanish.types';
import { ESiteType, ISite } from 'types/site.types';
import { SiteTypeLabels } from 'utils/site.utils';

interface IManageSiteSummaryProps {
  site: ISite;
  requiredSiteType?: ESiteType;
  requiredRawMaterialTitle?: string;
  requiredLocationName: string;
  errors: TManageSiteErrors;
}

export type TManageSiteErrors = { siteType?: string; rawMaterial?: string; country?: string };

const StyledItem = styled('li')<{ error: booleanish }>(({ theme, error }) => ({
  color: error === 'true' ? theme.custom.themeColors.error[100] : 'inherit',
}));

const ClusterContainer = styled(FlexBox)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.custom.themeColors.primary[5],
  borderRadius: 6,
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  width: '100%',
  flexDirection: 'column',
}));

const ManageSiteSummary: FC<IManageSiteSummaryProps> = ({
  site,
  requiredSiteType,
  requiredRawMaterialTitle,
  requiredLocationName,
  errors,
}) => {
  const isCluster = site.siteType === ESiteType.CLUSTER;

  return (
    <ClusterContainer>
      <FlexBox alignSelf="flex-start" mb={0.5}>
        <ThemeTypography variant="BODY_MEDIUM_BOLD">
          {isCluster
            ? 'Select sites which belong to this cluster:'
            : 'Select which cluster this site should belong to:'}
        </ThemeTypography>
      </FlexBox>

      <FlexBox width="100%" gap={2} alignItems="flex-start">
        <CardContainer sx={{ flex: 0.5 }} hasShadow={false} padding={1.5} hasBorder>
          <FlexBox display="flex" alignItems="flex-start" gap={2}>
            {!!site.image ? (
              <Image width={44} height={44} image={site.image} />
            ) : (
              <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                <Icon
                  name={isCluster ? 'site-cluster-placeholder' : 'site-placeholder'}
                  size="xx-large"
                  color="blue-ice"
                />
              </Box>
            )}

            <FlexBox flexDirection="column" alignItems="flex-start">
              <FlexBox gap={1}>
                <ThemeTypography variant="ITEM_TITLE">{site.title}</ThemeTypography>
                {site.externalId && (
                  <>
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_40" autoOverflow>
                      |
                    </ThemeTypography>
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_40" autoOverflow>
                      {site.externalId}
                    </ThemeTypography>
                  </>
                )}
              </FlexBox>

              <FlexBox gap={1}>
                <FlexBox gap={0.5}>
                  <Icon name="tag-filled" size="small" color="gray-60" />
                  <ThemeTypography variant="LABEL_INPUT">
                    {SiteTypeLabels[site.siteType]}
                  </ThemeTypography>
                </FlexBox>

                {isCluster && (
                  <FlexBox gap={0.5}>
                    <Icon name="farm" size="medium" color="gray-60" />
                    <ThemeTypography variant="LABEL_INPUT">
                      {site.sites.count}{' '}
                      {site.sitesType
                        ? `${SiteTypeLabels[site.sitesType].toLowerCase()}s`
                        : 'sites'}
                    </ThemeTypography>
                  </FlexBox>
                )}

                {site.siteType === ESiteType.FARM && (
                  <FlexBox gap={0.5}>
                    <Icon name="raw-material" size="medium" color="gray-60" />
                    <ThemeTypography variant="LABEL_INPUT">
                      {site.rawMaterial.title}
                    </ThemeTypography>
                  </FlexBox>
                )}

                <FlexBox gap={0.5}>
                  <Icon name="location" size="small" color="gray-60" />
                  <ThemeTypography variant="LABEL_INPUT">{site.locationName}</ThemeTypography>
                </FlexBox>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </CardContainer>

        <FlexBox flex={0.5}>
          <ThemeTypography variant="BODY_SMALL" color="BLUE_100">
            Sites must share certain properties to be in the same origin cluster:
            {requiredSiteType && (
              <StyledItem error={Booleanish(errors.siteType)}>
                <b>Site type:</b> {SiteTypeLabels[requiredSiteType]}
              </StyledItem>
            )}
            <StyledItem error={Booleanish(errors.country)}>
              <b>Location:</b> Within the same country ({requiredLocationName})
            </StyledItem>
            {requiredSiteType === ESiteType.FARM && (
              <StyledItem error={Booleanish(errors.rawMaterial)}>
                <b>Focus crop/material:</b> {requiredRawMaterialTitle}
              </StyledItem>
            )}
          </ThemeTypography>
        </FlexBox>
      </FlexBox>
    </ClusterContainer>
  );
};

export default ManageSiteSummary;
